/**
 * Connect a Gmail or Outlook mailbox from the dashboard without leaving it.
 *
 * The provider's consent screen opens in a popup. The backend's in-app flow (`reconnect=true`)
 * forces consent, so a fresh refresh token is issued, leaves the session cookie alone, and sends
 * the popup back to `return_to` with `<provider>_connect=true|false`. The popup hands that result
 * to the dashboard and closes itself. A blocked popup falls back to the same flow in this tab,
 * which returns to the page the rep is on.
 *
 * The result travels on a BroadcastChannel, not `window.opener`: the providers' sign-in pages
 * set a Cross-Origin-Opener-Policy that cuts the popup off from the tab that opened it.
 */

export type MailboxProvider = 'google' | 'microsoft';

/** Marks the page the popup lands on, so the shell relays the result instead of rendering. */
const POPUP_PARAM = 'mailbox_popup';
const CHANNEL = 'commander:mailbox-connect';

export interface MailboxConnectResult {
  provider: MailboxProvider;
  success: boolean;
}

function connectUrl(provider: MailboxProvider, returnTo: string): string {
  const rawBase = import.meta.env.VITE_API_BASE_URL || '/api';
  const base = rawBase.endsWith('/') ? rawBase.slice(0, -1) : rawBase;
  return `${base}/auth/${provider}?reconnect=true&return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Open the provider's consent screen in a popup; `onResult` runs once the popup reports back.
 * Returns a cleanup that stops listening. Call it inside the click, or the browser blocks the popup.
 */
export function connectMailbox(
  provider: MailboxProvider,
  onResult: (result: MailboxConnectResult) => void
): () => void {
  const popup = window.open(
    connectUrl(provider, `${window.location.pathname}?${POPUP_PARAM}=1`),
    'commander-mailbox-connect',
    'popup,width=520,height=680'
  );
  if (!popup) {
    window.location.assign(
      connectUrl(provider, `${window.location.pathname}${window.location.search}`)
    );
    return () => {
      // This tab is leaving for the provider: nothing to stop listening to.
    };
  }

  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event: MessageEvent<MailboxConnectResult>) => {
    channel.close();
    onResult({ provider: event.data.provider, success: Boolean(event.data.success) });
  };
  popup.focus();
  return () => channel.close();
}

/**
 * In the popup, once the provider sends it back: hand the result to the dashboard and close.
 * True when this window is that popup, so the shell renders a short note instead of the app
 * (shown only if the browser refuses to close the window).
 */
export function relayMailboxConnectResult(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get(POPUP_PARAM) !== '1') return false;
  const provider: MailboxProvider = params.has('microsoft_connect') ? 'microsoft' : 'google';
  const success = params.get(`${provider}_connect`) === 'true';
  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage({ provider, success } satisfies MailboxConnectResult);
  channel.close();
  window.close();
  const root = document.getElementById('root');
  if (root) {
    root.textContent = success
      ? 'Mailbox connected. You can close this window.'
      : 'The mailbox was not connected. Close this window and try again.';
    root.style.cssText = 'font: 14px system-ui, sans-serif; padding: 24px;';
  }
  return true;
}
